import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input, OnInit,
  Output,
  Renderer2,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { Itinerary, ItineraryAttraction } from '../../../models/itinerary';
import { SharedModule } from '../../../../shared/shared.module';
import { VisitorPlace } from '../../../../shared/models/visitor.place';
import { VisitorPlaceService } from '../../../../event-calendar/services/visitor.place.service';
import { GoogleMapComponent } from '../../../../shared/components/google.map/google.map.component';
import { of, takeUntil } from 'rxjs';
import { ScriptService } from '../../../../../common/services/load.script/load.script.service';
import { TripPlannerService } from '../../../services/trip.planner.service';
import { CommonModule } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { removeHtmlTags } from '../../../../../common/utils/string.extensions';
import { MediaQueryService } from '../../../../../common/services/media.query/media.query.service';
import { BaseComponent } from '../../../../../common/components/base.component';

@Component({
  selector: 'ig-tour-movie-simulation',
  standalone: true,
  imports: [
    SharedModule,
    CommonModule
  ],
  templateUrl: './tour.simulation.component.html',
  styleUrl: './tour.simulation.component.scss',
  // encapsulation: ViewEncapsulation.None
})
export class TourSimulationComponent extends BaseComponent implements OnInit {
  @Input() currentItinerary: Itinerary = null;
  @Input() mode: 'map' | 'story' = 'map';
  @Input() mapStyle: 'dark' | 'light' = 'dark';

  isMobile = false;
  isMovieSimulateTourShowing = false;
  isMovieDisplayingAttractions = false;
  attractionIndexInTourSimulation = 0;
  attractionPreInTourSimulation = false;
  isPreQuiting = false;
  attractionDisplayInTourSimulation = false;
  isAttractionQuiting = false;
  attractionPostInTourSimulation = false;
  isPostQuiting = false;
  attractionConnectionInTourSimulation = false;
  isConnectionQuiting = false;
  @Input() preloadImageCount = 5;
  // @Input() frontendSettings: IFrontendSettings;
  @Input() googleMap: GoogleMapComponent;
  @Input() directionResults: google.maps.DirectionsResult;
  @Input() directionLines: google.maps.Polyline[] = [];

  @Output() onStart = new EventEmitter();
  @Output() onEnd = new EventEmitter();

  attractionConnection = '';
  travelModeIcon = '';

  @ViewChild('simulateTextContainer', {static: false}) simulateTextContainer: ElementRef;

  placeService = inject(VisitorPlaceService);
  scriptService = inject(ScriptService);
  tripService = inject(TripPlannerService);
  renderer = inject(Renderer2);
  snackBar = inject(MatSnackBar);
  mediaQueryService = inject(MediaQueryService);

  constructor() {
    super();
  }

  stop() {
    if (this.isMovieSimulateTourShowing) {
      this.isMovieSimulateTourShowing = false;
      this.isMovieDisplayingAttractions = false;
      this.attractionConnectionInTourSimulation = false;
      this.attractionPreInTourSimulation = false;
      this.attractionPostInTourSimulation = false;
      this.attractionIndexInTourSimulation = 0;
      this.removeDirectionLines();
      this.getEle('.ig-iti-circle').classList.add('hidden');
      this.googleMap.googleMap.googleMap.setTilt(0);
      this.googleMap.directionsResults$ = of(this.directionResults);
      setTimeout(() => {
        this.googleMap.fitBounds();
        this.googleMap.googleMap.googleMap.setZoom(this.googleMap.googleMap.googleMap.getZoom() - 0.5);
      }, 1000);

      this.onEnd.emit();
    }
  }

  async movie360() {
    if (this.isMovieSimulateTourShowing) {
      this.stop();
      return;
    }

    const run = async () => {
      this.onStart.emit();
      this.googleMap?.closeInfoWindow();
      if (this.isMovieSimulateTourShowing) return;
      this.isMovieSimulateTourShowing = true;

      this.leanMap();

      setTimeout(async () => {
        // mobile view only has a small area for map when virtual tour, so we need to recenter the map (cancel the panto right)
        this.isMobile && this.googleMap.fitBounds();
        this.displayTitle();
      }, 1000);

      setTimeout(async () => {
        this.displayDescription();
      }, 2000);
    };

    // const SCRIPT_PATH = `https://cdnjs.cloudflare.com/ajax/libs/animejs/2.0.2/anime.min.js`;
    const SCRIPT_PATH = `https://cdn.jsdelivr.net/npm/animejs@3.2.2/lib/anime.min.js`;
    const scriptElement = this.scriptService.loadJsScript(
      this.renderer,
      SCRIPT_PATH,
      false
    );
    scriptElement.onload = () => {
      console.log('anime script loaded');

      this.hideRoute();

      setTimeout(() => {
        run();
      });
    };
    scriptElement.onerror = () => {
      console.log('Could not load the script: ' + SCRIPT_PATH);

      this.snackBar.open('Could not load the animate script', 'Close', {
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: 'ig-error-snackbar',
      });
    };
  }

  async test() {
    this.isMovieSimulateTourShowing = true;
    this.isMovieDisplayingAttractions = true;
    this.onStart.emit();
    this.attractionIndexInTourSimulation = 0;
    this.googleMap?.closeInfoWindow();

    this.hideRoute();
    this.leanMap();
    setTimeout(() => {
      this.showAttractionCircle();
    }, 6000);

    await this.displayAttractions();
  }

  private leanMap() {
    this.googleMap.fitBounds();
    this.googleMap.googleMap.googleMap.setZoom(this.googleMap.googleMap.googleMap.getZoom() - 0.5);
    this.googleMap.googleMap.googleMap.panBy(-150, 0);
    this.googleMap.googleMap.googleMap.setTilt(60);
  }

  private showAttractionCircle() {
    setTimeout(async () => {
      const lat = this.currentItinerary.attractions[this.attractionIndexInTourSimulation].item.address.lat;
      const lng = this.currentItinerary.attractions[this.attractionIndexInTourSimulation].item.address.lng;

      const start = await this.googleMap.getPixelPositionByLatLng(
        new google.maps.LatLng(lat, lng)
      );

      const circle = this.getEle('.ig-iti-circle');
      let x = start.x, y = start.y;

      if (this.isMobile) {
        const first = this.getEle(`div[aria-label="${this.attractionIndexInTourSimulation + 1}"]`);
        console.log('first', first);
        const rect = first.getBoundingClientRect();
        console.log('rect', rect);
        x = rect.x;
        y = rect.y;
        circle.style.position = 'fixed';
        circle.style.left = x - 13 + 'px';
        circle.style.top = y - 13 + 'px';
      } else {
        circle.style.left = x - 50 + 'px';
        circle.style.top = y - 69.5 + 'px';
      }

      circle.classList.remove('hidden');
    }, this.attractionIndexInTourSimulation === 0 ? 1000 : 0);
  }

  private hideRoute() {
    this.googleMap.directionsResults$ = of(null);
  }

  getEle(selector) {
    return document.querySelector('.cdk-overlay-container ig-itinerary-details-modal').shadowRoot.querySelector(selector);
  }

  getEleAll(selector) {
    return document.querySelector('.cdk-overlay-container ig-itinerary-details-modal').shadowRoot.querySelectorAll(selector);
  }

  displayTitle() {
    const textWrapper = this.getEle('.ig-iti-sim-title .ig-letters');
    textWrapper.innerHTML = textWrapper.textContent.replace(/\S/g, "<span class='ig-letter'>$&</span>");
    (textWrapper as HTMLElement).style.opacity = '1';
    const letter = this.getEleAll('.ig-iti-sim-title .ig-letter');
    const ml10 = this.getEleAll('.ig-iti-sim-title');

    window['anime'].timeline({loop: false})
      .add({
        targets: letter,
        rotateY: [-90, 0],
        duration: 1300,
        delay: (el, i) => 45 * i
      }).add({
      targets: ml10,
      // opacity: 0,
      duration: 1000,
      easing: "easeOutExpo",
      delay: 1000
    });
  }

  displayInfo() {
    const info = [
      `${this.currentItinerary.attractions.length} Stops`,
      this.tripService.convertToTravelDistance(this.currentItinerary.distance).all,
      this.tripService.convertToTravelTimeText(this.currentItinerary.travelTime) + ' ' + this.currentItinerary.defaultTravelMode?.mode?.toLowerCase() || '',
      ...this.currentItinerary.customFields.map(x => x.field)
    ].map(x => {
      return x.replace(/\S/g, "<span class='ig-iti-letter'>$&</span>").replace(/<\/span> <span/g, '</span>&nbsp;<span');
    });

    let timeout = 0;

    const run = (content: string) => {
      this.simulateTextContainer.nativeElement.innerHTML = content;
      const letters = this.getEleAll('.ig-iti-sim-card-con .ig-iti-letter');

      this.leftInAnimate(letters);
    };

    for (let i = 0; i < info.length; i++) {
      timeout = i === 0 ? 0 : (timeout + 4400);

      setTimeout(() => {
        run(info[i]);
      }, timeout);
    }

    setTimeout(async () => {
      await this.displayAttractions();
    }, timeout + 4400);
  }

  displayDescription() {
    let timeout = 0;

    if (this.currentItinerary.description) {
      // replace some special characters. e.g. .) -> ). Because . ? and ! are used to split sentences
      const text = this.currentItinerary.description.replace(/.\)/g, ')')
        .replace(/\?\)/g, ')')
        .replace(/\\!\)/g, ')')
        .replace(/."/g, '"')
        .replace(/\?"/g, '"')
        .replace(/\!"/g, '"');
      const sentences = text.split(/\.|\?|\!/).filter(x => !!x.trim()).map(x => x + '.'); // Split on periods, question marks, or exclamation points

      const run = (sentence: string) => {
        const words = sentence.split(' ').filter(x => !!x.trim());

        this.simulateTextContainer.nativeElement.innerHTML = words.map(x => {
          return `<span class='ig-iti-letter'>${x}</span>`;
        }).join('&nbsp;');

        const letters = this.getEleAll('.ig-iti-sim-card-con .ig-iti-letter');
        this.leftInAnimate(letters);
      };

      for (let i = 0; i < sentences.length; i++) {
        timeout = i === 0 ? 0 : (timeout + sentences[i - 1].split(' ').length * 70 + 3500);

        setTimeout(() => {
          run(sentences[i]);
        }, timeout);

        if (i === sentences.length - 1) {
          timeout += sentences[i].split(' ').length * 70 + 3500;
        }
      }
    }

    setTimeout(() => {
      this.displayInfo();
    }, timeout);
  }

  async displayAttractions() {
    this.isMovieDisplayingAttractions = true;
    this.attractionIndexInTourSimulation = 0;

    const legs = this.directionResults.routes[0].legs;

    await this.displayMovieMapAttractionCard();

    for (let legIndex = 0; legIndex < legs.length; legIndex++) {
      if (this.quitIfUserStopTour()) return;

      const leg = legs[legIndex];

      setTimeout(async () => {
        this.attractionIndexInTourSimulation++;
        await this.displayMovieMapAttractionCard(leg);
      });

      let waitFor = this.currentItinerary.attractions[this.attractionIndexInTourSimulation]?.preDescription ? (5000 + 280) : 0;
      waitFor += 9000 + 280;
      waitFor += this.currentItinerary.attractions[this.attractionIndexInTourSimulation]?.postDescription ? (5000 + 280) : 0;
      waitFor += leg ? (3000 + 280) : 0;

      let timeSpent = 0;

      for (let j = 0; j < leg.steps.length; j++) {
        if (this.quitIfUserStopTour()) return;

        const step = leg.steps[j];

        for (let i = 0; i < step.path.length; i++) {
          if (this.quitIfUserStopTour()) return;

          const path = step.path[i];

          if (i < step.path.length - 1) {
            const pathNext = step.path[i + 1];

            this.drawRouteLine({
              start: {lat: path.lat(), lng: path.lng()},
              end: {lat: pathNext.lat(), lng: pathNext.lng()},
            });

            timeSpent += 20;
            await this.myWait(20);
          }
        }
      }

      if (this.quitIfUserStopTour()) return;

      if (waitFor - timeSpent > 0) {
        await this.myWait(waitFor - timeSpent);
      }
    }

    this.isMovieDisplayingAttractions = false;
    this.isMovieSimulateTourShowing = false;
    this.attractionIndexInTourSimulation = 0;
    this.googleMap.googleMap.googleMap.setTilt(0);
    // show the default Google direction routes
    this.googleMap.directionsResults$ = of(this.directionResults);
    this.googleMap.googleMap.googleMap.setZoom(this.googleMap.googleMap.googleMap.getZoom() - 0.5);
    this.removeDirectionLines();
    const circle = this.getEle('.ig-iti-circle');
    circle.classList.add('hidden');
    this.onEnd.emit();
  }

  removeDirectionLines() {
    for (const line of this.directionLines) {
      line.setMap(null);
    }
    this.directionLines = [];
  }

  quitIfUserStopTour() {
    if (!this.isMovieSimulateTourShowing) {
      this.isConnectionQuiting = false;
      this.attractionConnectionInTourSimulation = false;
      this.attractionPreInTourSimulation = false;
      this.isAttractionQuiting = false;
      this.attractionDisplayInTourSimulation = false;
      this.attractionPostInTourSimulation = false;
      this.isConnectionQuiting = false;
      return true;
    }
    return false;
  }

  private async displayMovieMapAttractionCard(leg?: google.maps.DirectionsLeg) {
    if (this.quitIfUserStopTour()) return;

    this.showAttractionCircle();

    if (leg) {
      this.attractionConnectionInTourSimulation = true;
      this.travelModeIcon = this.currentItinerary.defaultTravelMode.mode === 'WALKING' ? 'directions_walk' : this.currentItinerary.defaultTravelMode.mode === 'BICYCLING' ? 'directions_bike' : 'directions_car';
      this.attractionConnection = `${leg.distance.text}, ${leg.duration.text}`;
      await this.myWait(3000);
      if (this.quitIfUserStopTour()) return;
      this.isConnectionQuiting = true;
      await this.myWait(280);
      this.attractionConnectionInTourSimulation = false;
      this.isConnectionQuiting = false;
    }

    // setTimeout(() => {
    //   // this.leanMap();
    //   // this.showAttractionCircle();
    // });

    if (this.currentItinerary.attractions[this.attractionIndexInTourSimulation]?.preDescription) {
      this.attractionPreInTourSimulation = true;
      await this.myWait(5000);
      if (this.quitIfUserStopTour()) return;
      this.isPreQuiting = true;
      await this.myWait(280);
      this.attractionPreInTourSimulation = false;
      this.isPreQuiting = false;
    }

    this.attractionDisplayInTourSimulation = true;
    await this.myWait(9000);
    if (this.quitIfUserStopTour()) return;
    this.isAttractionQuiting = true;
    await this.myWait(280);
    this.attractionDisplayInTourSimulation = false;
    this.isAttractionQuiting = false;

    if (this.currentItinerary.attractions[this.attractionIndexInTourSimulation]?.postDescription) {
      this.attractionPostInTourSimulation = true;
      await this.myWait(5000);
      if (this.quitIfUserStopTour()) return;
      this.isPostQuiting = true;
      await this.myWait(280);
      this.attractionPostInTourSimulation = false;
      this.isPostQuiting = false;
    }
  }

  removeHtml(content: string) {
    return removeHtmlTags(content);
  }

  drawRouteLine(data: {
    start: {lat: number, lng: number};
    end?: {lat: number, lng: number};
  }) {
    const flightPlanCoordinates = [
      {lat: data.start.lat, lng: data.start.lng},
      {lat: data.end.lat, lng: data.end.lng},
    ];
    const flightPath = new google.maps.Polyline({
      path: flightPlanCoordinates,
      geodesic: true,
      strokeColor: this.mapStyle === 'dark' ? '#b5ca2d' : 'deepskyblue',//'#0064b6',
      // strokeOpacity: 0.8,
      strokeWeight: 3,
    });

    flightPath.setMap(this.googleMap.googleMap.googleMap);
    this.directionLines.push(flightPath);
  }

  leftInAnimate(letters: NodeListOf<Element>) {
    window['anime'].timeline({loop: false})
      .add({
        targets: letters,
        translateX: [40, 0],
        opacity: [0, 1],
        easing: "easeOutExpo",
        duration: 1200,
        delay: (el, i) => 500 + 30 * i
      })
      .add({
        targets: letters,
        translateX: [0, -10],
        opacity: [1, 0],
        easing: "easeInExpo",
        duration: 2100,
        delay: (el, i) => 100 + 10 * i
      });
  }

  getImages(item: ItineraryAttraction): string[] {
    if (item?.feedType === 'place') {
      const attraction = item.item as VisitorPlace;
      const allowedImages =
        attraction.allowedGooglePhotos &&
        attraction.allowedGooglePhotos.slice(0, this.preloadImageCount);

      if (allowedImages && allowedImages.length) {
        return allowedImages;
      }

      return attraction.photos
        .slice(0, this.preloadImageCount)
        .map((x) => {
          return this.placeService.getGoogleImgUrl(
            x.photo_reference,
            this.frontendSettings.customSettings.dataSource.googleKey
          );
        });
    }

    return [item.item.cover.source];
  }

  toReadable(code: string) {
    return this.placeService.toReadableCategory(code);
  }

  myWait(milSeconds) {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve(null);
      }, milSeconds);
    });
  }

  ngOnInit(): void {
    this.mediaQueryService.isMobile$
      .pipe(takeUntil(this.isDestroyed))
      .subscribe((isMobile) => {
        this.isMobile = isMobile;
      });
  }
}
